/**
 * SafetyAttestationSettings.jsx — admin-only editor for the OH&S / OSHA
 * fit-for-duty checklist that workers acknowledge at Start (Feb 2026).
 *
 * Lives in Settings → Compliance. Edits PUT /api/company/safety-attestation,
 * which bumps the `version` whenever items actually change so historical
 * attestations remain auditable.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Switch } from './ui/switch';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';
import { ShieldCheck, Plus, Trash2, RotateCcw, Loader2 } from 'lucide-react';

const API = `${window.__BACKEND_URL__}/api`;

const DEFAULT_ITEMS = [
  'I am fit for duty and capable of doing the work safely. If I am not able to work today, I will notify the company and let them know I cannot perform tasks today.',
  'I am competent in the skills required to perform the job.',
  'I am in good health.',
  'I have the right personal protective equipment (PPE) to perform the tasks.',
  'I have the right to refuse work if the work area is unsafe or there are unexpected hazards that have not been isolated to make the work area safe for me.',
];

export default function SafetyAttestationSettings() {
  const { toast } = useToast();
  const [enabled, setEnabled] = useState(true);
  const [items, setItems] = useState([]);
  const [version, setVersion] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    axios
      .get(`${API}/company/safety-attestation`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => {
        setEnabled(!!data.enabled);
        setItems(data.items?.length ? data.items : DEFAULT_ITEMS);
        setVersion(data.version || 1);
      })
      .catch(() => {
        setItems(DEFAULT_ITEMS);
      })
      .finally(() => setLoading(false));
  }, []);

  const save = async (patch) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.put(
        `${API}/company/safety-attestation`,
        patch,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (data.items) setItems(data.items);
      if (typeof data.enabled === 'boolean') setEnabled(data.enabled);
      if (data.version) setVersion(data.version);
      toast({ title: 'Saved', description: 'Safety attestation updated.' });
    } catch (e) {
      toast({
        title: 'Could not save',
        description: e.response?.data?.detail || e.message,
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const updateItem = (i, val) => setItems((prev) => prev.map((it, idx) => (idx === i ? val : it)));
  const removeItem = (i) => setItems((prev) => prev.filter((_, idx) => idx !== i));
  const addItem = () => setItems((prev) => [...prev, '']);

  if (loading) {
    return (
      <Card className="shadow-lg">
        <CardContent className="p-6 flex items-center gap-2 text-sm text-slate-400">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading safety settings…
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="shadow-lg" data-testid="safety-attestation-settings">
      <CardHeader className="bg-[#1A2433] border-b">
        <CardTitle className="text-lg font-bold flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-emerald-500" />
          Safety Attestation (OH&amp;S / OSHA)
        </CardTitle>
        <p className="text-xs text-slate-400 mt-1">
          Workers must acknowledge every item below before a job transitions to In Progress. Edits
          bump the attestation version so older accepted records remain auditable. Current version:{' '}
          <span className="font-mono text-slate-200">v{version}</span>
        </p>
      </CardHeader>
      <CardContent className="p-5 space-y-5">
        <div className="flex items-center justify-between gap-4 p-3 rounded-md border border-slate-700 bg-slate-900/40">
          <div className="flex-1">
            <p className="text-sm font-semibold text-slate-100">Require attestation at job start</p>
            <p className="text-xs text-slate-400 mt-0.5">
              When off, jobs can be started without the safety pop-up. Recommended: keep on.
            </p>
          </div>
          <Switch
            checked={enabled}
            onCheckedChange={(v) => save({ enabled: !!v })}
            disabled={saving}
            data-testid="safety-attestation-enabled-switch"
          />
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-slate-100">Checklist items</p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => { setItems(DEFAULT_ITEMS); toast({ title: 'Reverted', description: 'Defaults restored — click Save to publish.' }); }}
              disabled={saving}
              data-testid="safety-attestation-defaults-btn"
            >
              <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
              Reset to OSHA / OH&amp;S defaults
            </Button>
          </div>

          {items.map((it, i) => (
            <div key={i} className="flex gap-2 items-start" data-testid={`safety-attestation-item-${i}`}>
              <span className="text-xs text-slate-500 font-mono pt-3 select-none">{i + 1}.</span>
              <Textarea
                value={it}
                onChange={(e) => updateItem(i, e.target.value)}
                placeholder="e.g. I have completed the toolbox safety meeting for this site."
                className="flex-1 min-h-[60px]"
                maxLength={500}
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => removeItem(i)}
                disabled={saving || items.length <= 1}
                className="text-rose-400 hover:bg-rose-500/10 mt-1"
                data-testid={`safety-attestation-remove-${i}`}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addItem}
            disabled={saving || items.length >= 20}
            data-testid="safety-attestation-add-btn"
          >
            <Plus className="w-3.5 h-3.5 mr-1.5" />
            Add item
          </Button>
        </div>

        <div className="flex justify-end gap-2 pt-3 border-t border-slate-800">
          <Button
            type="button"
            onClick={() => save({ items })}
            disabled={saving || items.some((x) => !x.trim())}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="safety-attestation-save-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
            {saving ? 'Saving…' : 'Save checklist'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
